import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Package, Truck, Home, Sparkles } from "lucide-react";
import { api, inr } from "@/lib/api";

const STAGES = ["Order Placed", "Payment Confirmed", "Processing", "Shipped", "Delivered"];

export default function OrderTracking() {
  const { orderNo } = useParams();
  const [sp] = useSearchParams();
  const [orderNoInput, setOrderNoInput] = useState(orderNo || "");
  const [phone, setPhone] = useState("");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const fetchOrder = async (no = orderNoInput, ph = phone) => {
    setErr("");
    try {
      const params = ph ? `?phone=${encodeURIComponent(ph)}` : "";
      const { data } = await api.get(`/orders/track/${no}${params}`);
      setData(data);
    } catch (e) { setErr(e.response?.data?.detail || "Not found"); setData(null); }
  };

  useEffect(() => { if (orderNo) fetchOrder(orderNo); }, [orderNo]);

  const stageIdx = data ? (() => {
    if (data.status === "cancelled") return -2; // sentinel for cancelled
    const tl = (data.timeline || []).map((t) => t.stage.toLowerCase());
    let idx = -1;
    STAGES.forEach((s, i) => { if (tl.some((t) => t.includes(s.toLowerCase()))) idx = i; });
    const statusMap = { pending: 0, processing: 2, shipped: 3, delivered: 4 };
    const fromStatus = statusMap[data.status];
    if (typeof fromStatus === "number" && fromStatus > idx) idx = fromStatus;
    return idx;
  })() : -1;
  const cancelled = stageIdx === -2;

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">
      <div className="eyebrow mb-2">Order Tracking</div>
      <h1 className="font-serif text-3xl md:text-4xl text-[#0B1F3A] mb-8">Track your order</h1>
      {sp.get("just_placed") && (
        <div className="bg-[#D4AF37]/10 p-4 mb-6 gold-border">
          <div className="flex items-center gap-2 text-[#0B1F3A] font-semibold"><Sparkles size={16} /> Order placed successfully</div>
        </div>
      )}
      <div className="bg-white p-6 gold-border mb-8">
        <div className="grid md:grid-cols-3 gap-3">
          <input value={orderNoInput} onChange={(e) => setOrderNoInput(e.target.value)} placeholder="Order ID (e.g. NL-88492)" className="border px-3 py-2.5 text-sm" style={{ borderColor: "rgba(212,175,55,0.4)" }} data-testid="track-orderno" />
          <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (optional)" className="border px-3 py-2.5 text-sm" style={{ borderColor: "rgba(212,175,55,0.4)" }} data-testid="track-phone" />
          <button onClick={() => fetchOrder()} className="bg-[#0B1F3A] text-[#FFF9E6] py-2.5 tracking-widest uppercase text-sm" data-testid="track-btn">Track</button>
        </div>
        {err && <div className="text-red-600 text-sm mt-3">{err}</div>}
      </div>

      {data && (
        <div className="bg-white p-6 gold-border">
          {cancelled && (
            <div className="bg-red-50 border border-red-200 text-red-800 text-sm px-3 py-2 mb-4" data-testid="order-cancelled">
              This order has been cancelled.
            </div>
          )}
          <div className="flex justify-between mb-6">
            <div>
              <div className="eyebrow mb-1">Order</div>
              <div className="font-serif text-xl text-[#0B1F3A]" data-testid="order-no">{data.order_no}</div>
            </div>
            <div className="text-right">
              <div className="eyebrow mb-1">Total</div>
              <div className="font-serif text-xl text-[#D4AF37]">{inr(data.total)}</div>
            </div>
          </div>

          {/* Desktop: horizontal 5-step */}
          <div className="hidden sm:block relative py-8">
            <div className="absolute left-4 right-4 top-[52px] h-0.5 bg-[#D4AF37]/20" />
            <div className="grid grid-cols-5 relative">
              {STAGES.map((s, i) => {
                const done = i <= stageIdx;
                return (
                  <div key={s} className="flex flex-col items-center gap-2">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${done ? "bg-[#D4AF37] border-[#D4AF37] text-[#0B1F3A]" : "bg-white border-[#D4AF37]/30 text-[#1E293B]/30"}`}>
                      {[<Sparkles size={14} />, <CheckCircle2 size={14} />, <Package size={14} />, <Truck size={14} />, <Home size={14} />][i]}
                    </div>
                    <div className={`text-[10px] tracking-wider text-center ${done ? "text-[#0B1F3A] font-semibold" : "text-[#1E293B]/40"}`}>{s}</div>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Mobile: vertical timeline */}
          <div className="sm:hidden py-4">
            {STAGES.map((s, i) => {
              const done = i <= stageIdx;
              const icon = [<Sparkles size={14} />, <CheckCircle2 size={14} />, <Package size={14} />, <Truck size={14} />, <Home size={14} />][i];
              return (
                <div key={s} className="flex gap-3 relative">
                  <div className="flex flex-col items-center">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 shrink-0 ${done ? "bg-[#D4AF37] border-[#D4AF37] text-[#0B1F3A]" : "bg-white border-[#D4AF37]/30 text-[#1E293B]/30"}`}>{icon}</div>
                    {i < STAGES.length - 1 && <div className={`w-0.5 flex-1 my-1 ${done && i < stageIdx ? "bg-[#D4AF37]" : "bg-[#D4AF37]/20"}`} style={{ minHeight: 24 }} />}
                  </div>
                  <div className={`flex-1 pb-4 pt-1.5 ${done ? "" : "opacity-50"}`}>
                    <div className={`text-sm font-semibold ${done ? "text-[#0B1F3A]" : "text-[#1E293B]/60"}`}>{s}</div>
                    {done && (data.timeline || []).find((t) => t.stage.toLowerCase().includes(s.toLowerCase())) && (
                      <div className="text-[10px] text-[#1E293B]/50 tracking-wide uppercase mt-0.5">
                        {new Date((data.timeline || []).find((t) => t.stage.toLowerCase().includes(s.toLowerCase())).at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 text-sm">
            <div className="eyebrow mb-2">Delivery to</div>
            <div className="text-[#1E293B]/80">{data.address.full_name}, {data.address.line1}, {data.address.city}, {data.address.state} - {data.address.pincode}</div>
          </div>

          {data.tracking_url && (
            <a href={data.tracking_url} target="_blank" rel="noreferrer" className="inline-block mt-4 bg-[#0B1F3A] text-[#FFF9E6] px-6 py-2 text-xs tracking-widest uppercase" data-testid="ext-track">External tracking</a>
          )}
        </div>
      )}
    </div>
  );
}
